import type {
  AccountType,
  AssetRests,
  MarginAccount,
  MarginAssetRests,
  RegularAccount,
} from '@/entities/account';
import { NUMERIC_CONFIG } from '@/shared/config';
import type {
  AssetsSymbols,
  DictionaryResponse,
  TransferInternalSettingsDictionary,
} from '@/shared/types';
import { useMemo } from 'react';
import type {
  InternalAccountSectionCardType,
  SectionType,
  InternalTransferAccountsMap,
  AssetOptions,
  AssetSelectOption,
  AssetOption,
} from './types';
import { useInternalTransferFillOptions } from './useInternalTransferFillOptions';

interface UseInternalTransferAssetsArgs {
  account: InternalAccountSectionCardType;
  sectionType: SectionType;
  isAssetSelector: boolean;
  baseAsset: string;
  assetsIconsData?: Record<string, string>;
  assetsSymbolsData?: DictionaryResponse<AssetsSymbols>;
  transferInternalSettings?: TransferInternalSettingsDictionary;
  selectedAccountTypeFrom?: AccountType | null;
  selectedAccountTypeTo?: AccountType | null;
  watchedAssetFromCode?: string;
  watchedAssetToCode?: string;
  watchedAccNumFrom?: string;
  watchedAccNumTo?: string;
  accountInfoMap: InternalTransferAccountsMap;
  selectedAssetFrom?: AssetOption | undefined;
}

interface UseInternalTransferAssetsResult {
  assetSelectOptions: AssetSelectOption[];
  accountWithAssetOptions: AssetSelectOption[];
}

export function useInternalTransferAssets(
  args: UseInternalTransferAssetsArgs
): UseInternalTransferAssetsResult {
  const {
    account,
    sectionType,
    isAssetSelector,
    baseAsset,
    assetsIconsData,
    assetsSymbolsData,
    transferInternalSettings,
    selectedAccountTypeFrom,
    selectedAccountTypeTo,
    watchedAssetFromCode,
    watchedAssetToCode,
    watchedAccNumFrom,
    watchedAccNumTo,
    accountInfoMap,
    selectedAssetFrom,
  } = args;

  const { fillOptionsArray } = useInternalTransferFillOptions();

  const assetOptions = useMemo<AssetOptions>(() => {
    if (
      !account.data ||
      (account.type !== 'spot' && account.type !== 'ewallet')
    ) {
      return [];
    }

    const existedAccount = account.data.accounts[0] as
      | RegularAccount
      | undefined;
    if (!existedAccount?.assets) return [];

    let filteredAssets = existedAccount.assets.filter(
      (asset: AssetRests) => asset.available > 0
    );

    if (
      sectionType === 'to' &&
      watchedAssetFromCode &&
      assetsSymbolsData &&
      assetsSymbolsData.value
    ) {
      filteredAssets = filteredAssets.filter(asset => {
        const { code } = asset;

        if (code === watchedAssetFromCode) {
          return true;
        }

        return assetsSymbolsData.value.some(symbol => {
          return (
            (symbol.asset1.code === watchedAssetFromCode &&
              symbol.asset2.code === code) ||
            (symbol.asset2.code === watchedAssetFromCode &&
              symbol.asset1.code === code)
          );
        });
      });
    }

    if (
      sectionType === 'from' &&
      watchedAssetToCode &&
      transferInternalSettings &&
      assetsSymbolsData &&
      assetsSymbolsData.value
    ) {
      filteredAssets = filteredAssets.filter(asset => {
        const { code } = asset;

        if (selectedAccountTypeTo) {
          const withdrawSettings =
            transferInternalSettings[
              selectedAccountTypeTo as 'spot' | 'invest' | 'leverage' | 'earn'
            ];

          if (
            withdrawSettings &&
            !withdrawSettings.isInternalWithdrawConvertAllowed &&
            code === watchedAssetToCode
          ) {
            return true;
          }
        }

        if (code === watchedAssetToCode) {
          return true;
        }

        return assetsSymbolsData.value.some(symbol => {
          return (
            (symbol.asset1.code === watchedAssetToCode &&
              symbol.asset2.code === code) ||
            (symbol.asset2.code === watchedAssetToCode &&
              symbol.asset1.code === code)
          );
        });
      });
    }

    if (
      sectionType === 'to' &&
      watchedAssetFromCode &&
      transferInternalSettings &&
      selectedAccountTypeFrom &&
      (selectedAccountTypeFrom === 'spot' ||
        selectedAccountTypeFrom === 'invest' ||
        selectedAccountTypeFrom === 'leverage')
    ) {
      const withdrawSettings =
        transferInternalSettings[
          selectedAccountTypeFrom as 'spot' | 'invest' | 'leverage' | 'earn'
        ];

      if (
        withdrawSettings &&
        !withdrawSettings.isInternalWithdrawConvertAllowed
      ) {
        filteredAssets = filteredAssets.filter(
          asset => asset.code === watchedAssetFromCode
        );
      }
    }

    if (
      sectionType === 'to' &&
      watchedAssetFromCode &&
      filteredAssets.length === 0 &&
      ((account.type === 'spot' &&
        transferInternalSettings?.spot?.isInternalDepositAllowed) ||
        account.type === 'ewallet')
    ) {
      let syntheticFromName = watchedAssetFromCode;
      let syntheticFromPrecision: number = NUMERIC_CONFIG.precision.default;

      if (assetsSymbolsData && assetsSymbolsData.value) {
        const symbol = assetsSymbolsData.value.find(
          symbol =>
            symbol.asset1.code === watchedAssetFromCode ||
            symbol.asset2.code === watchedAssetFromCode
        );

        const matchedAsset =
          symbol?.asset1.code === watchedAssetFromCode
            ? symbol.asset1
            : symbol?.asset2;

        if (matchedAsset) {
          syntheticFromName = matchedAsset.name || watchedAssetFromCode;
          syntheticFromPrecision =
            matchedAsset.precision || NUMERIC_CONFIG.precision.default;
        }
      }

      filteredAssets = [
        {
          code: watchedAssetFromCode,
          name: syntheticFromName,
          available: 0,
          baseAvailable: 0,
          precision: syntheticFromPrecision,
        } as AssetRests,
      ];
    }

    return filteredAssets.map((asset: AssetRests) => {
      const isBaseAsset = asset.code === baseAsset;
      return {
        code: asset.code,
        name: asset.name,
        available: asset.available,
        precision: asset.precision || NUMERIC_CONFIG.precision.default,
        icon: assetsIconsData?.[asset.code] || '',
        baseAvailable: asset.baseAvailable,
        isBaseAsset,
      };
    });
  }, [
    account.data,
    account.type,
    assetsIconsData,
    baseAsset,
    sectionType,
    assetsSymbolsData,
    transferInternalSettings,
    selectedAccountTypeFrom,
    selectedAccountTypeTo,
    selectedAssetFrom,
    watchedAssetFromCode,
    watchedAssetToCode,
  ]);

  const accountWithAssetOptions = useMemo<AssetSelectOption[]>(() => {
    if (
      !account.data ||
      (account.type !== 'invest' &&
        account.type !== 'leverage' &&
        account.type !== 'earn') ||
      !transferInternalSettings
    ) {
      return [];
    }

    let primary: AssetSelectOption[] = [];
    let secondary: AssetSelectOption[] = [];

    account.data.accounts.forEach((acc: RegularAccount | MarginAccount) => {
      if (acc.assets && acc.assets.length > 0) {
        const accountInfo = accountInfoMap.get(acc.accNum);
        acc.assets.forEach((asset: AssetRests | MarginAssetRests) => {
          primary.push({
            margin:
              'marginIndicators' in acc
                ? acc.marginIndicators?.margin
                : undefined,
            equity:
              'marginIndicators' in acc
                ? acc.marginIndicators?.equity
                : undefined,
            ...asset,
            available: 'equity' in asset ? asset.equity : asset.available,
            accName: accountInfo?.accName,
            accNum: acc.accNum,
            isBaseAsset: asset.code === baseAsset,
            accTypeName: accountInfo?.accTypeName,
            tradingProductCode: accountInfo?.tradingProductCode,
            leverage: accountInfo?.leverage
              ? {
                  value: accountInfo.leverage.value,
                  name: accountInfo.leverage.name,
                }
              : null,
            value: `${acc.accNum}_${asset.code}`,
            label: `${asset.code} - ${accountInfo?.accTypeName || ''} ${
              acc.accNum
            }`.trim(),
          });
        });
      }
    });

    const withdrawSettings =
      transferInternalSettings[
        selectedAccountTypeFrom as
          | 'spot'
          | 'invest'
          | 'leverage'
          | 'earn'
          | 'earn'
      ];

    const isWithdrawConvertNotAllowed =
      withdrawSettings && !withdrawSettings.isInternalWithdrawConvertAllowed;

    const isInvest =
      account.type === 'invest' && selectedAccountTypeFrom === 'invest';

    const isLeverage =
      account.type === 'leverage' && selectedAccountTypeFrom === 'leverage';

    const isEarn =
      account.type === 'earn' && selectedAccountTypeFrom === 'earn';

    if (sectionType === 'from') {
      primary = primary.filter(option => {
        const quantity = option.available ?? option.equity ?? 0;
        return quantity > 0;
      });

      if ((isInvest || isLeverage || isEarn) && watchedAccNumTo) {
        primary = primary.filter(option => option.accNum !== watchedAccNumTo);
        secondary = [];
      }

      if (isWithdrawConvertNotAllowed) {
        if (watchedAssetToCode) {
          primary = primary.filter(
            option => option.code === watchedAssetToCode
          );
        }
        secondary = [];
      } else if (watchedAssetToCode) {
        secondary = primary.filter(
          option => option.code !== watchedAssetToCode
        );
        primary = primary.filter(option => option.code === watchedAssetToCode);
      }
    }

    const isAssetFromSelectedAndSectionTo =
      sectionType === 'to' &&
      watchedAssetFromCode &&
      assetsSymbolsData &&
      assetsSymbolsData.value;

    if (isAssetFromSelectedAndSectionTo) {
      if (isWithdrawConvertNotAllowed) {
        secondary = [];
      } else {
        secondary = primary.filter(option => {
          const assetCode = option.code;

          return assetsSymbolsData.value.some(symbol => {
            return (
              (symbol.asset1.code === watchedAssetFromCode &&
                symbol.asset2.code === assetCode) ||
              (symbol.asset2.code === watchedAssetFromCode &&
                symbol.asset1.code === assetCode)
            );
          });
        });
      }

      if (
        ((isInvest && selectedAccountTypeTo === 'invest') ||
          (isLeverage && selectedAccountTypeTo === 'leverage') ||
          (isEarn && selectedAccountTypeTo === 'earn')) &&
        watchedAccNumFrom
      ) {
        primary = primary.filter(option => option.accNum !== watchedAccNumFrom);
        if (
          (isInvest && selectedAccountTypeTo === 'invest') ||
          (isEarn && selectedAccountTypeTo === 'earn')
        ) {
          secondary = [];
        }
      }

      primary = primary.filter(option => {
        return option.code === watchedAssetFromCode;
      });
    }

    return fillOptionsArray(primary, secondary);
  }, [
    account.data,
    account.type,
    baseAsset,
    accountInfoMap,
    sectionType,
    watchedAssetFromCode,
    watchedAssetToCode,
    assetsSymbolsData,
    transferInternalSettings,
    selectedAccountTypeFrom,
    selectedAccountTypeTo,
    watchedAccNumFrom,
    watchedAccNumTo,
  ]);

  const assetSelectOptions = useMemo(() => {
    if (!isAssetSelector) {
      return [];
    }

    const baseOptions = assetOptions.map(opt => ({
      value: opt.code,
      label: opt.name,
      code: opt.code,
      name: opt.name,
      icon: opt.icon,
      quantity: opt.available,
      baseQuantity: opt.baseAvailable,
      isBaseAsset: opt.isBaseAsset,
      precision: opt.precision,
    }));

    if (!transferInternalSettings) {
      return baseOptions;
    }

    if (sectionType === 'from') {
      const withdrawSettings =
        transferInternalSettings[
          selectedAccountTypeFrom as 'spot' | 'invest' | 'leverage' | 'earn'
        ];

      const isWithdrawConvertNotAllowed =
        withdrawSettings &&
        !withdrawSettings.isInternalWithdrawConvertAllowed &&
        watchedAssetToCode;

      if (!watchedAssetToCode) {
        return baseOptions;
      }

      if (isWithdrawConvertNotAllowed) {
        return baseOptions.filter(option => option.code === watchedAssetToCode);
      }

      const primary = baseOptions.filter(
        opt => opt.code === watchedAssetToCode
      );
      const secondary = baseOptions.filter(
        opt => opt.code !== watchedAssetToCode
      );

      if (!primary.length && isWithdrawConvertNotAllowed) {
        return baseOptions;
      }

      return fillOptionsArray(primary, secondary);
    }

    if (sectionType === 'to' && watchedAssetFromCode) {
      let primary: AssetSelectOption[] = [];
      let secondary: AssetSelectOption[] = [];

      const withdrawSettings =
        transferInternalSettings[
          selectedAccountTypeTo as 'spot' | 'invest' | 'leverage' | 'earn'
        ];

      const isDepositConvertAllowed =
        ((withdrawSettings &&
          withdrawSettings.isInternalDepositConvertAllowed) ||
          selectedAccountTypeTo === 'ewallet') &&
        !!watchedAssetFromCode;

      primary = baseOptions.filter(opt => opt.code === watchedAssetFromCode);
      secondary = baseOptions.filter(opt => opt.code !== watchedAssetFromCode);

      if (
        isDepositConvertAllowed &&
        primary.length === 0 &&
        watchedAssetFromCode &&
        selectedAssetFrom
      ) {
        primary = [
          {
            value: selectedAssetFrom?.code,
            label: selectedAssetFrom?.code,
            code: selectedAssetFrom?.code,
            name: selectedAssetFrom?.name,
            icon: assetsIconsData?.[selectedAssetFrom?.code] || '',
            quantity: 0,
            baseQuantity: 0,
            isBaseAsset: selectedAssetFrom?.code === 'USDT',
            precision:
              selectedAssetFrom?.precision || NUMERIC_CONFIG.precision.default,
          },
        ];
      }

      const hasUsdt = [...primary, ...secondary].some(
        opt => opt.code === 'USDT'
      );

      if (!hasUsdt && baseOptions?.length > 1 && isDepositConvertAllowed) {
        secondary = [
          ...secondary,
          {
            value: 'USDT',
            label: 'USDT',
            code: 'USDT',
            name: 'Tether',
            icon: assetsIconsData?.USDT || '',
            quantity: 0,
            baseQuantity: 0,
            isBaseAsset: false,
            precision: NUMERIC_CONFIG.precision.default,
          },
        ];
      }

      return fillOptionsArray(primary, secondary);
    }

    return baseOptions;
  }, [
    assetOptions,
    isAssetSelector,
    sectionType,
    watchedAssetFromCode,
    watchedAssetToCode,
    transferInternalSettings,
    selectedAssetFrom,
    selectedAccountTypeFrom,
    selectedAccountTypeTo,
    assetsIconsData,
  ]);

  return {
    assetSelectOptions,
    accountWithAssetOptions,
  };
}
